import React, { useState, useRef, useContext, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Select, MenuItem,
    InputLabel, FormControl, Paper, Stack, styled
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { GoogleMap, useJsApiLoader, Marker, Autocomplete } from '@react-google-maps/api';
import SearchBar from './FormsSearchBar';
import FormsBottomNavbar from './FormsBottomNavbar';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './../../AuthContext/AuthContext';
import { BASE_URL } from './../../Api/ApiUrls';
import { IconButton, InputAdornment } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { GOOGLEMAPS_API_KEY } from './../../Api/ApiUrls';
import GuestLoginModal from '../../GuestLoginModal';
import { CircularProgress } from '@mui/material';
import SuccessAlert from '../../../Components/Users/Alertpage/SuccessAlert/SuccessAlert';
import FailureAlert from '../../../Components/Users/Alertpage/FailureAlert/FailureAlert';
import FullScreenLoader from '../../FullScreenLoader';

const GOOGLE_MAPS_API_KEY = GOOGLEMAPS_API_KEY;

const containerStyle = {
    width: '100%',
    height: '300px',
};

const centerDefault = {
    lat: 18.387449,
    lng: 78.803236,
};

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const RedButton = styled(Button)({
    backgroundColor: 'red',
    color: 'white',
    '&:hover': { backgroundColor: '#cc0000' },
});

const GreenButton = styled(Button)({
    backgroundColor: 'green',
    color: 'white',
    '&:hover': { backgroundColor: '#008000' },
});

const VendorRegister = () => {

    const { userId, logout, isGuest } = useContext(AuthContext);
    const [errors, setErrors] = useState({});
    const [showGuestModal, setShowGuestModal] = useState(false);
    const mapRef = useRef(null);
    const [apiHitCount, setApiHitCount] = useState(0);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: ['places'],
    });

    const [address, setAddress] = useState('');
    const [locationConfirmed, setLocationConfirmed] = useState(false);
    const [markerIcon, setMarkerIcon] = useState(null);
    const [usingCurrentLocation, setUsingCurrentLocation] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showFailure, setShowFailure] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");

    useEffect(() => {
        if (isGuest) {
            setShowGuestModal(true);
        }
    }, [isGuest]);

    useEffect(() => {
        if (isLoaded && window.google) {
            setMarkerIcon({
                url: "data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20fill%3D%22%23ff0000%22%20d%3D%22M12%202C8.13%202%205%205.13%205%209c0%205.25%207%2013%207%2013s7-7.75%207-13c0-3.87-3.13-7-7-7zm0%209.5c-1.38%200-2.5-1.12-2.5-2.5s1.12-2.5%202.5-2.5%202.5%201.12%202.5%202.5-1.12%202.5-2.5%202.5z%22%2F%3E%3C%2Fsvg%3E",
                scaledSize: new window.google.maps.Size(40, 40),
                origin: new window.google.maps.Point(0, 0),
                anchor: new window.google.maps.Point(20, 40)
            });
        }
    }, [isLoaded]);

    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            setUsingCurrentLocation(true);
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const pos = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    setLocation(pos);

                    try {
                        const response = await fetch(
                            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${pos.lat},${pos.lng}&key=${GOOGLE_MAPS_API_KEY}`
                        );
                        const data = await response.json();

                        if (data.status == 'OK' && data.results.length > 0) {
                            const addr = data.results[0].formatted_address;
                            setAddress(addr);
                            setErrors(prev => ({ ...prev, address: '' }));
                        } else {
                            setAddress(`Lat: ${pos.lat.toFixed(6)}, Lng: ${pos.lng.toFixed(6)}`);
                        }
                    } catch (error) {
                        console.error('Google Maps API error:', error);
                        setAddress(`Lat: ${pos.lat.toFixed(6)}, Lng: ${pos.lng.toFixed(6)}`);
                    }
                },
                (error) => {
                    console.error("Error getting current location:", error);
                    alert("Error getting current location. Please enable location services.");
                }
            );
        } else {
            alert("Geolocation is not supported by this browser.");
        }
    };

    const handleMapClick = async (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setLocation({ lat, lng });
        setUsingCurrentLocation(false);
        incrementApiHit();

        if (window.google && window.google.maps) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status == 'OK' && results[0]) {
                    setAddress(results[0].formatted_address);
                } else {
                    setAddress(`Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`);
                }
            });
        }
    };

    const incrementApiHit = () => {
        setApiHitCount(prev => prev + 1);
        console.log(`Google Maps API hits: ${apiHitCount + 1}`);
    };

    const [formData, setFormData] = useState({
        name: '',
        user_id: '',
        profession: '',
        mobile: '',
        email: '',
        address: '',
        experience: '',
        description: '',
        lat: '',
        long: ''
    });

    const [profilePhoto, setProfilePhoto] = useState(null);
    const [workPhotos, setWorkPhotos] = useState([]);
    const [location, setLocation] = useState(centerDefault);
    const autocompleteRef = useRef(null);
    const navigate = useNavigate();

    // ✅ Validate a single field on change
    const validateField = (fieldName, value) => {
        let error = "";

        switch (fieldName) {
            case "name":
                if (!value.trim()) {
                    error = "Name is required";
                } else if (!/^[A-Za-z ]+$/.test(value)) {
                    error = "Only letters are allowed";
                } else if (value.trim().length < 3) {
                    error = "Name must be at least 3 characters";
                }
                break;

            case "mobile":
                if (!value.trim()) {
                    error = "Mobile number is required";
                } else if (!/^[6-9]\d{9}$/.test(value)) {
                    error = "Enter valid 10-digit mobile number";
                }
                break;

            case "email":
                if (!value.trim()) {
                    error = "Email is required";
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    error = "Enter a valid email address";
                }
                break;

            case "experience":
                if (!value.toString().trim()) {
                    error = "Experience is required";
                } else if (isNaN(value) || Number(value) < 0) {
                    error = "Enter a valid number of years";
                }
                break;

            case "description":
                if (!value.trim()) {
                    error = "Description is required";
                } else if (value.trim().length < 10) {
                    error = "Description must be at least 10 characters";
                }
                break;

            default:
                break;
        }

        setErrors(prev => ({ ...prev, [fieldName]: error }));
        return error;
    };

    // ✅ Validate ALL fields before submit
    const validateForm = () => {
        const newErrors = {};

        if (!formData.profession) {
            newErrors.profession = "Please select a profession";
        }
        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
        } else if (!/^[A-Za-z ]+$/.test(formData.name)) {
            newErrors.name = "Only letters are allowed";
        } else if (formData.name.trim().length < 3) {
            newErrors.name = "Name must be at least 3 characters";
        }
        if (!formData.mobile.trim()) {
            newErrors.mobile = "Mobile number is required";
        } else if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
            newErrors.mobile = "Enter valid 10-digit mobile number";
        }
        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Enter a valid email address";
        }
        if (!address.trim()) {
            newErrors.address = "Please select a location";
        }
        if (!formData.experience.toString().trim()) {
            newErrors.experience = "Experience is required";
        } else if (isNaN(formData.experience) || Number(formData.experience) < 0) {
            newErrors.experience = "Enter a valid number of years";
        }
        if (!profilePhoto) {
            newErrors.profilePhoto = "Profile photo is required";
        }
        if (!formData.description.trim()) {
            newErrors.description = "Description is required";
        } else if (formData.description.trim().length < 10) {
            newErrors.description = "Description must be at least 10 characters";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // ✅ Block non-numeric input for experience and mobile
        if (name === 'experience') {
            if (value !== '' && !/^\d*$/.test(value)) return;
        }
        if (name === 'mobile') {
            if (value !== '' && !/^\d*$/.test(value)) return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
        validateField(name, value);
    };

    const handleProfilePhotoChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setProfilePhoto(e.target.files[0]);
            setErrors(prev => ({ ...prev, profilePhoto: '' }));
        }
    };

    const handleWorkPhotosChange = (e) => {
        if (e.target.files) {
            setWorkPhotos(Array.from(e.target.files));
        }
    };

    const onPlaceChanged = () => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();
            if (place && place.geometry) {
                const newLat = place.geometry.location.lat();
                const newLng = place.geometry.location.lng();
                setLocation({ lat: newLat, lng: newLng });
                setAddress(place.formatted_address);
                setErrors(prev => ({ ...prev, address: '' }));
                setFormData(prev => ({
                    ...prev,
                    address: place.formatted_address,
                    lat: newLat,
                    long: newLng
                }));
            }
        }
    };

    const geocodeAddress = async () => {
        if (window.google && window.google.maps) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address: formData.address }, (results, status) => {
                if (status == 'OK' && results[0]) {
                    const loc = results[0].geometry.location;
                    const lat = loc.lat();
                    const lng = loc.lng();
                    setLocation({ lat, lng });
                    setFormData(prev => ({
                        ...prev,
                        address: results[0].formatted_address,
                        lat,
                        long: lng
                    }));
                }
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isGuest) return;

        // ✅ Block submit if validation fails
        if (!validateForm()) return;

        setLoading(true);

        try {
            const formDataToSend = new FormData();

            const updatedFormData = {
                ...formData,
                user_id: userId,
                lat: location.lat,
                long: location.lng,
                address: address,
            };

            Object.keys(updatedFormData).forEach((key) => {
                formDataToSend.append(key, updatedFormData[key]);
            });

            if (profilePhoto) {
                formDataToSend.append("profile", profilePhoto);
            }

            workPhotos.forEach((photo) => {
                formDataToSend.append("new_work_images", photo);
            });

            const response = await fetch(`${BASE_URL}/vendors/`, {
                method: "POST",
                body: formDataToSend,
            });

            if (response.ok) {
                const data = await response.json();
                setAlertMessage("Vendor registered successfully!");
                setShowSuccess(true);
            } else {
                const errorData = await response.json();
                console.error("Error response:", errorData);
                setAlertMessage("Form submission failed. Check console for details.");
                setShowFailure(true);
            }

        } catch (error) {
            console.error("Network or other error:", error);
            setAlertMessage("Error submitting form. Please try again.");
            setShowFailure(true);
        } finally {
            setLoading(false);
        }
    };

    const handleBackClick = () => navigate(-1);
    const handleSearchClick = () => console.log('Search icon clicked');
    const handleFilterClick = () => console.log('Filter icon clicked');

    const workerTypes = [
        "Painting", "Carpenter", "Flooring", "AC Technician", "Cleaning maid",
        "Gardener", "Sofa Cleaning", "Water Purifier", "Kitchen/Toilet Cleaning", "Plumbing", "Electrical"
    ];

    if (!isLoaded) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Typography>Loading Google Maps...</Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1200, backgroundColor: "#fff", boxShadow: 1 }}>
                <SearchBar
                    onBackClick={handleBackClick}
                    onSearchClick={handleSearchClick}
                    onFilterClick={handleFilterClick}
                />
            </Box>

            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                pt: { xs: '60px', sm: '100px' },
                backgroundColor: 'rgb(239, 231, 221)',
            }}>
                <Box sx={{
                    flexGrow: 1,
                    p: { xs: 2, sm: 3 },
                    pb: 10,
                    display: 'flex',
                    justifyContent: 'center',
                }}>
                    <Paper
                        elevation={2}
                        sx={{ p: { xs: 2, sm: 3 }, mb: 5, width: '100%', boxSizing: 'border-box' }}
                        component="form"
                        onSubmit={handleSubmit}
                    >
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3, textAlign: 'center' }}>
                            Vendor Registration Form
                        </Typography>

                        {/* ✅ Profession — required */}
                        <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.profession}>
                            <InputLabel id="category-label">Select Profession *</InputLabel>
                            <Select
                                labelId="category-label"
                                label="Select Profession *"
                                name="profession"
                                value={formData.profession}
                                onChange={(e) => {
                                    handleInputChange(e);
                                    setErrors(prev => ({ ...prev, profession: '' }));
                                }}
                            >
                                {workerTypes.map((type, index) => (
                                    <MenuItem key={index} value={type}>{type}</MenuItem>
                                ))}
                            </Select>
                            {errors.profession && (
                                <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>
                                    {errors.profession}
                                </Typography>
                            )}
                        </FormControl>

                        {/* ✅ Name — letters only, min 3 chars */}
                        <TextField
                            fullWidth
                            label="Name *"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            variant="outlined"
                            sx={{ mb: 2 }}
                            error={!!errors.name}
                            helperText={errors.name || ''}
                            inputProps={{ maxLength: 50 }}
                        />

                        {/* ✅ Mobile — digits only, 10 digits starting 6-9 */}
                        <TextField
                            fullWidth
                            label="Mobile *"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleInputChange}
                            variant="outlined"
                            sx={{ mb: 2 }}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 10 }}
                            error={!!errors.mobile}
                            helperText={errors.mobile || ''}
                        />

                        {/* ✅ Email — format validation */}
                        <TextField
                            fullWidth
                            label="Email *"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            variant="outlined"
                            sx={{ mb: 2 }}
                            error={!!errors.email}
                            helperText={errors.email || ''}
                        />

                        {/* ✅ Location — required */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Location
                            </Typography>

                            <Autocomplete
                                onLoad={(ref) => (autocompleteRef.current = ref)}
                                onPlaceChanged={onPlaceChanged}
                            >
                                <TextField
                                    fullWidth
                                    label="Search Location *"
                                    variant="outlined"
                                    value={address}
                                    onChange={(e) => {
                                        setAddress(e.target.value);
                                        if (e.target.value.trim()) {
                                            setErrors(prev => ({ ...prev, address: '' }));
                                        }
                                    }}
                                    onBlur={geocodeAddress}
                                    error={!!errors.address}
                                    helperText={errors.address || ''}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={getCurrentLocation} edge="end">
                                                    <MyLocationIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Autocomplete>

                            <div style={{ ...containerStyle, position: 'relative', marginTop: '8px' }}>
                                <GoogleMap
                                    mapContainerStyle={{ width: '100%', height: '100%' }}
                                    center={location}
                                    zoom={15}
                                    options={{ gestureHandling: 'greedy' }}
                                    onLoad={(map) => { mapRef.current = map; }}
                                    onDragStart={() => setLocationConfirmed(false)}
                                />

                                {/* Fixed pin at center */}
                                <div style={{
                                    position: 'absolute', top: '50%', left: '50%',
                                    transform: 'translate(-50%, -100%)', pointerEvents: 'none', zIndex: 10,
                                }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
                                        <path fill="#ff0000" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                                    </svg>
                                </div>

                                {/* Confirm location button */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (window.google && window.google.maps && mapRef.current) {
                                            const center = mapRef.current.getCenter();
                                            const lat = center.lat();
                                            const lng = center.lng();
                                            setLocation({ lat, lng });
                                            const geocoder = new window.google.maps.Geocoder();
                                            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                                                if (status === 'OK' && results[0]) {
                                                    const selectedAddress = results[0].formatted_address;
                                                    setAddress(selectedAddress);
                                                    setErrors(prev => ({ ...prev, address: '' }));
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        location: selectedAddress,
                                                        lat: lat.toString(),
                                                        long: lng.toString(),
                                                    }));
                                                } else {
                                                    const fallback = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
                                                    setAddress(fallback);
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        location: fallback,
                                                        lat: lat.toString(),
                                                        long: lng.toString(),
                                                    }));
                                                }
                                                setLocationConfirmed(true);
                                            });
                                        }
                                    }}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        backgroundColor: locationConfirmed ? 'green' : 'red',
                                        border: 'none', borderRadius: '50%', width: '44px', height: '44px',
                                        cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                                        position: 'absolute', right: '10px', top: '45%',
                                        transform: 'translateY(-50%)', zIndex: 10,
                                        transition: 'background-color 0.3s ease',
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24">
                                        <path fill="white" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                    </svg>
                                </button>
                            </div>
                        </Box>

                        {/* ✅ Experience — digits only */}
                        <TextField
                            fullWidth
                            label="Work Experience (years) *"
                            name="experience"
                            value={formData.experience}
                            onChange={handleInputChange}
                            variant="outlined"
                            sx={{ mb: 2, mt: 1 }}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 2 }}
                            error={!!errors.experience}
                            helperText={errors.experience || ''}
                        />

                        {/* ✅ Profile Photo — required */}
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Profile Photo *
                        </Typography>
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            fullWidth
                            sx={{
                                mb: 1,
                                borderColor: errors.profilePhoto ? '#d32f2f' : undefined,
                                color: errors.profilePhoto ? '#d32f2f' : undefined,
                            }}
                        >
                            Upload Profile Photo
                            <VisuallyHiddenInput
                                type="file"
                                accept="image/*"
                                onChange={handleProfilePhotoChange}
                            />
                        </Button>
                        {errors.profilePhoto && (
                            <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mb: 1, ml: 1.5 }}>
                                {errors.profilePhoto}
                            </Typography>
                        )}
                        {profilePhoto && (
                            <Typography variant="caption" display="block" gutterBottom>
                                Selected: {profilePhoto.name}
                            </Typography>
                        )}

                        {/* Previous Work Photos */}
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>
                            Previous Worked Photos
                        </Typography>
                        <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth sx={{ mb: 2 }}>
                            Upload Previous Photos
                            <VisuallyHiddenInput
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleWorkPhotosChange}
                            />
                        </Button>
                        {workPhotos.length > 0 && (
                            <Typography variant="caption" display="block" gutterBottom>
                                Selected: {workPhotos.length} file(s)
                            </Typography>
                        )}

                        {/* ✅ Description — required, min 10 chars */}
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Description *
                        </Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            multiline
                            rows={4}
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            error={!!errors.description}
                            helperText={errors.description || ''}
                            sx={{ mb: 2 }}
                        />

                        <>
                            {/* Full Screen Loader */}
                            <FullScreenLoader open={loading} text="Submitting..." />

                            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                                <RedButton
                                    variant="contained"
                                    size="large"
                                    sx={{ px: 4, fontWeight: 'bold' }}
                                    onClick={() => navigate(-1)}
                                    disabled={loading}
                                >
                                    Cancel
                                </RedButton>

                                <GreenButton
                                    variant="contained"
                                    size="large"
                                    type="submit"
                                    disabled={isGuest || loading}
                                    sx={{
                                        px: 4,
                                        fontWeight: 'bold',
                                        backgroundColor: (isGuest || loading) ? '#ccc' : undefined,
                                        color: (isGuest || loading) ? '#777' : undefined,
                                        cursor: (isGuest || loading) ? 'not-allowed' : 'pointer',
                                        '&:hover': {
                                            backgroundColor: (isGuest || loading) ? '#ccc' : undefined,
                                        },
                                    }}
                                >
                                    {isGuest ? "Login to Submit" : "Submit"}
                                </GreenButton>
                            </Stack>
                        </>
                    </Paper>
                    <Box sx={{ height: '70px' }} />
                </Box>
            </Box>

            <GuestLoginModal
                open={showGuestModal}
                onClose={() => setShowGuestModal(false)}
            />

            {showSuccess && (
                <SuccessAlert
                    message={alertMessage}
                    onClose={() => {
                        setShowSuccess(false);
                        navigate("/dashboard");
                    }}
                />
            )}

            {showFailure && (
                <FailureAlert
                    message={alertMessage}
                    onClose={() => {
                        console.log("Closing failure alert");
                        setShowFailure(false);
                        setAlertMessage("");
                        navigate("/vendor-form");
                    }}
                />
            )}

            <FormsBottomNavbar />
        </>
    );
};

export default VendorRegister;